import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { PostHogProvider } from './lib/analytics';
import { I18nProvider } from './lib/i18n';
import { ThemeProvider, PRERENDER_THEME } from './lib/theme';
import { isLocale } from './lib/locale-path';
import App from './App';
import './styles/global.scss';

// Hydrate only a page prerendered for this very path. The same index.html is
// also served for app routes, whose markup it does not contain.
const container = document.getElementById('root')!;
const prerendered = document.querySelector<HTMLMetaElement>('meta[name="prerendered-path"]')?.content;
const here = window.location.pathname.replace(/\/+$/, '') || '/';
const hydrate = !!prerendered && prerendered === here;

// When hydrating, the first render must reproduce the prerendered markup: its
// locale (a visitor who prefers another language is redirected by
// LocaleRouteSync right after) and the theme it was rendered with.
const htmlLang = document.documentElement.lang;
const prerenderedLocale = hydrate && isLocale(htmlLang) ? htmlLang : undefined;

const app = (
  <StrictMode>
    <ThemeProvider initialResolvedTheme={hydrate ? PRERENDER_THEME : undefined}>
      <I18nProvider initialLocale={prerenderedLocale}>
        <PostHogProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </PostHogProvider>
      </I18nProvider>
    </ThemeProvider>
  </StrictMode>
);

if (hydrate) hydrateRoot(container, app);
else createRoot(container).render(app);
